import { useSyncExternalStore } from "react";

// Общее окно подтверждения панели. Вызывается из любого раздела:
//
//   if (!(await confirmAction({ title, message, confirmText, variant: "danger" }))) return;
//
// А если действие должно пройти, пока окно открыто, — со спиннером на кнопке
// и закрытием по его окончании, — оно передаётся в onConfirm:
//
//   await confirmAction({ title, confirmText, onConfirm: () => declineOffer() });
//
// Окно одно на всю панель (ConfirmDialogHost в App), поэтому все
// подтверждения выглядят одинаково. Новый вызов, пока открыт прежний,
// закрывает прежний как отменённый.
//
// variant: "danger" — удаление и прочее необратимое (красная кнопка и пометка
// «Действие необратимо», её убирает note: false или заменяет свой текст),
// "accent" — обычное подтверждение. icon: "trash" | "send" | "alert" | "logout".

let current = null;
let seq = 0;
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

export const confirmAction = (options = {}) => new Promise((resolve) => {
  if (current && !current.closed) current.resolve(false);
  seq += 1;
  current = {
    id: seq,
    title: String(options.title || "").trim(),
    message: options.message || "",
    confirmText: String(options.confirmText || "").trim(),
    cancelText: String(options.cancelText || "").trim(),
    variant: options.variant === "accent" ? "accent" : "danger",
    icon: options.icon || "",
    note: options.note,
    onConfirm: typeof options.onConfirm === "function" ? options.onConfirm : null,
    busy: false,
    closed: false,
    resolve
  };
  notify();
});

const update = (patch) => {
  if (!current) return;
  current = { ...current, ...patch };
  notify();
};

export const settleConfirm = async (accepted) => {
  const entry = current;
  if (!entry || entry.closed || entry.busy) return;
  if (accepted && entry.onConfirm) {
    update({ busy: true });
    let ok = true;
    try {
      await entry.onConfirm();
    } catch (_) {
      // Об ошибке действие сообщает само; окно просто закрывается.
      ok = false;
    }
    if (current?.id !== entry.id) return;
    update({ busy: false, closed: true });
    entry.resolve(ok);
    return;
  }
  update({ closed: true });
  entry.resolve(Boolean(accepted));
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useConfirmState = () => useSyncExternalStore(subscribe, () => current, () => current);
